<script setup lang="ts">
// @ts-nocheck — three@0.164 未自带类型声明，且内网镜像暂无 @types/three
// 数字人核心技术圆环：圆环生成算法、多材质着色器与头部模型交互
// 仅做 Vue 化封装（生命周期管理 + DOM 查询限定在组件根节点内），交互逻辑与参数保持不变。
import { onBeforeUnmount, onMounted, ref } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

interface RingModule {
  index: string
  title: string
  detail: string
  mode: string
  ring: number
  position: number
  size: number
}

interface RingSetting {
  thickness: number
  count: number
  gap: number
  start: number
  speed: number
  rotation: number
  distribution: 'random' | 'even'
  randomness: number
}

const stageRef = ref<HTMLElement>()
const modelSlotRef = ref<HTMLElement>()
const ringSystemRef = ref<SVGSVGElement>()
const modelCanvasRef = ref<HTMLCanvasElement>()

let cleanupFns: Array<() => void> = []

/** 组件卸载时统一清理所有事件监听、动画帧与 Three.js 资源，避免内存泄漏 */
onBeforeUnmount(() => {
  cleanupFns.forEach((fn) => {
    try {
      fn()
    } catch (error) {
      console.error('[DigitalHumanTechRing] 清理资源失败：', error)
    }
  })
  cleanupFns = []
})

onMounted(async () => {
  try {
    await initRingInteraction()
    await initThreeScene()
  } catch (error) {
    console.error('[DigitalHumanTechRing] 初始化失败：', error)
  }
})

/** 初始化分段圆环的 SVG 生成与 hover/自动演示交互，逻辑与原 index.html 保持一致 */
async function initRingInteraction(): Promise<void> {
  const stage = stageRef.value
  const modelSlot = modelSlotRef.value
  const ringSystem = ringSystemRef.value
  if (!stage || !modelSlot || !ringSystem) return

  const svgNS = 'http://www.w3.org/2000/svg'
  const svgNode = (name: string, className?: string): SVGGraphicsElement => {
    const node = document.createElementNS(svgNS, name) as unknown as SVGGraphicsElement
    if (className) node.setAttribute('class', className)
    return node
  }
  const setSvgAttrs = (node: SVGGraphicsElement, attrs: Record<string, string | number>): SVGGraphicsElement => {
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)))
    return node
  }
  const createRingEffects = (): SVGGraphicsElement => {
    const defs = svgNode('defs')
    const edge = setSvgAttrs(svgNode('linearGradient'), { id: 'ring-hover-edge', x1: '0%', y1: '10%', x2: '100%', y2: '90%' })
    ;([['0%', '#79ecff'], ['26%', '#ffffff'], ['52%', '#c7a9ff'], ['76%', '#ff75d8'], ['100%', '#8cecff']] as const).forEach(([offset, color]) =>
      edge.append(setSvgAttrs(svgNode('stop'), { offset, 'stop-color': color }))
    )
    defs.append(edge)
    return defs
  }

  const ringLayout = { centerX: 0, centerY: 0, baseRadius: 168, layerGap: 0, hoverGrow: 37, lineColor: '#d9dade', lineWidth: .5, cornerRadius: 7, wave: 9, waveSpeed: .63 }
  const ringSettings: RingSetting[] = [
    { thickness: 36, count: 6, gap: 0, start: 258, speed: 1.05, rotation: -4, distribution: 'random', randomness: .62 },
    { thickness: 42, count: 7, gap: 0, start: 270, speed: -.72, rotation: 5, distribution: 'random', randomness: .68 },
    { thickness: 54, count: 8, gap: 0, start: 279, speed: -.38, rotation: 5, distribution: 'random', randomness: .72 }
  ]
  const ringModules: RingModule[] = [
    { index: '01', title: '定制视频生成', detail: '品牌化内容快速生产', mode: 'pixel', ring: 2, position: .06, size: 1 },
    { index: '02', title: '高精度唇形驱动', detail: '精准口型与音画同步', mode: 'scan', ring: 1, position: .31, size: 1 },
    { index: '03', title: 'AI native 集成调度', detail: 'agent自动化素材生产', mode: 'topology', ring: 1, position: .66, size: 1 },
    { index: '04', title: '低延迟实时交互', detail: '秒级响应连续互动', mode: 'ascii', ring: 1, position: .47, size: 1 },
    { index: '05', title: '2D数字人建模', detail: '构建形象资产', mode: 'wire', ring: 2, position: .48, size: 1 },
    { index: '06', title: '高保真人像生成', detail: '精细还原人物形象特征', mode: 'halftone', ring: 0, position: .25, size: 1 }
  ]

  interface RingSegment {
    ringIndex: number
    index: number
    start: number
    end: number
    inner: number
    outer: number
    content?: RingModule
    item?: SVGGraphicsElement
    path?: SVGGraphicsElement
    hoverPath?: SVGGraphicsElement
    titleGuide?: SVGGraphicsElement
    detailGuide?: SVGGraphicsElement
  }
  interface RingState {
    group: SVGGraphicsElement
    hit: SVGGraphicsElement
    innerOutline: SVGGraphicsElement
    outerOutline: SVGGraphicsElement
    segments: RingSegment[]
    setting: RingSetting
    rotation: number
    expansion: number
    hoverItem: SVGGraphicsElement | null
    currentInner?: number
    currentOuter?: number
    currentCenterX?: number
    currentCenterY?: number
  }

  const rings: RingState[] = []
  const ringItems: SVGGraphicsElement[] = []

  const polar = (cx: number, cy: number, r: number, angle: number): [number, number] => {
    const radians = angle * Math.PI / 180
    return [cx + Math.cos(radians) * r, cy + Math.sin(radians) * r]
  }
  const bandPath = (cx: number, cy: number, inner: number, outer: number, start: number, end: number, cornerRadius = 0): string => {
    const a = polar(cx, cy, outer, start); const b = polar(cx, cy, outer, end); const c = polar(cx, cy, inner, end); const d = polar(cx, cy, inner, start)
    const large = end - start > 180 ? 1 : 0
    const corner = Math.max(0, Math.min(cornerRadius, (outer - inner) * .45))
    if (corner < .1) return `M${a[0].toFixed(2)} ${a[1].toFixed(2)} A${outer} ${outer} 0 ${large} 1 ${b[0].toFixed(2)} ${b[1].toFixed(2)} L${c[0].toFixed(2)} ${c[1].toFixed(2)} A${inner} ${inner} 0 ${large} 0 ${d[0].toFixed(2)} ${d[1].toFixed(2)} Z`
    const span = end - start; const outerInset = Math.min(corner / outer * 180 / Math.PI, span * .22); const innerInset = Math.min(corner / inner * 180 / Math.PI, span * .22)
    const os = polar(cx, cy, outer, start + outerInset); const oe = polar(cx, cy, outer, end - outerInset); const outerEnd = polar(cx, cy, outer, end); const endOuter = polar(cx, cy, outer - corner, end); const endInner = polar(cx, cy, inner + corner, end); const innerEnd = polar(cx, cy, inner, end); const ie = polar(cx, cy, inner, end - innerInset); const is = polar(cx, cy, inner, start + innerInset); const innerStart = polar(cx, cy, inner, start); const startInner = polar(cx, cy, inner + corner, start); const startOuter = polar(cx, cy, outer - corner, start); const outerStart = polar(cx, cy, outer, start)
    const outerLarge = span - outerInset * 2 > 180 ? 1 : 0; const innerLarge = span - innerInset * 2 > 180 ? 1 : 0
    const p = (point: [number, number]): string => `${point[0].toFixed(2)} ${point[1].toFixed(2)}`
    return `M${p(os)} A${outer} ${outer} 0 ${outerLarge} 1 ${p(oe)} Q${p(outerEnd)} ${p(endOuter)} L${p(endInner)} Q${p(innerEnd)} ${p(ie)} A${inner} ${inner} 0 ${innerLarge} 0 ${p(is)} Q${p(innerStart)} ${p(startInner)} L${p(startOuter)} Q${p(outerStart)} ${p(os)} Z`
  }
  const textArc = (cx: number, cy: number, r: number, start: number, end: number, reverse: boolean): string => {
    const from = polar(cx, cy, r, reverse ? end : start); const to = polar(cx, cy, r, reverse ? start : end); const large = end - start > 180 ? 1 : 0
    return `M${from[0].toFixed(2)} ${from[1].toFixed(2)} A${r.toFixed(2)} ${r.toFixed(2)} 0 ${large} ${reverse ? 0 : 1} ${to[0].toFixed(2)} ${to[1].toFixed(2)}`
  }

  let locked: SVGGraphicsElement | null = null
  let autoItem: SVGGraphicsElement | null = null
  let autoOrder: SVGGraphicsElement[] = []
  let autoIndex = 0
  let hoverHeldItem: SVGGraphicsElement | null = null
  const autoIdleDelay = 3000; const autoHold = 3600; const autoInterval = 4600
  let lastUserActivity = Date.now()
  let lastAutoChange = lastUserActivity - (autoInterval - autoIdleDelay)
  let autoReleaseAt = 0
  /** 同环切换冷却，避免扇区交界处因膨胀/旋转改 path 导致 hover 来回跳 */
  let lastSwitchAt = 0
  const pointerState = { inside: false, x: 0, y: 0 }
  const hoverAnchor = { x: 0, y: 0 }
  const sameRingSwitchDistance = 42
  const sameRingSwitchCooldown = 280
  const interactiveItems = (): SVGGraphicsElement[] => [...ringItems]

  const activate = (item: SVGGraphicsElement | null): void => {
    interactiveItems().forEach((candidate) => {
      candidate.classList.toggle('active', candidate === item)
      candidate.setAttribute('aria-pressed', candidate === item ? 'true' : 'false')
    })
    stage.classList.toggle('has-active', !!item)
    modelSlot.dataset.mode = item?.dataset.mode || 'default'
  }
  /** 动画/选中只认 .active，避免 :hover 与 JS 状态抢控制权 */
  const isSelectedItem = (item?: SVGGraphicsElement | null): boolean => !!item?.classList.contains('active')
  const markUserActivity = (): void => {
    lastUserActivity = Date.now()
    lastAutoChange = lastUserActivity - (autoInterval - autoIdleDelay)
    autoReleaseAt = 0
    if (autoItem) { autoItem = null; if (!locked) activate(null) }
  }
  const refreshAutoOrder = (): void => {
    const visualTopToBottom = ['01', '06', '02', '03', '04', '05']
    autoOrder = visualTopToBottom.map((index) => ringItems.find((item) => item.dataset.moduleIndex === index)).filter(Boolean) as SVGGraphicsElement[]
    autoIndex = 0
  }
  const updateAutoDemo = (): void => {
    const now = Date.now()
    if (autoItem && now >= autoReleaseAt) { autoItem = null; autoReleaseAt = 0; if (!locked) activate(null) }
    if (stage.dataset.layout !== 'rings' || locked || hoverHeldItem || now - lastUserActivity < autoIdleDelay || now - lastAutoChange < autoInterval) return
    if (interactiveItems().some((item) => item.matches(':focus-visible')) || rings.some((ring) => ring.hit?.matches(':hover'))) return
    if (!autoOrder.length) refreshAutoOrder()
    const next = autoOrder[autoIndex % autoOrder.length]
    if (!next) return
    autoIndex += 1; autoItem = next; lastAutoChange = now; autoReleaseAt = now + autoHold
    const ringIndex = next.dataset.ringIndex
    if (ringIndex !== undefined && rings[Number(ringIndex)]) rings[Number(ringIndex)].hoverItem = next
    activate(next)
  }
  const bindInteractive = (item: SVGGraphicsElement): void => {
    item.addEventListener('pointerenter', (event) => {
      markUserActivity()
      // 已锁定时只保留锁定项，避免交界 hover 抢激活
      if (locked) {
        if (!locked.classList.contains('active')) activate(locked)
        return
      }
      const ringIndex = item.dataset.ringIndex
      if (ringIndex !== undefined && rings[Number(ringIndex)]) {
        const rect = stage.getBoundingClientRect()
        const nextX = (event as PointerEvent).clientX - rect.left
        const nextY = (event as PointerEvent).clientY - rect.top
        pointerState.inside = true
        pointerState.x = nextX
        pointerState.y = nextY
        const now = Date.now()
        const sameRing = hoverHeldItem && hoverHeldItem !== item && hoverHeldItem.dataset.ringIndex === ringIndex
        const tooClose = sameRing && Math.hypot(nextX - hoverAnchor.x, nextY - hoverAnchor.y) < sameRingSwitchDistance
        const cooling = sameRing && now - lastSwitchAt < sameRingSwitchCooldown
        if (tooClose || cooling) {
          rings[Number(ringIndex)].hoverItem = hoverHeldItem
          if (hoverHeldItem && !hoverHeldItem.classList.contains('active')) activate(hoverHeldItem)
          return
        }
        hoverHeldItem = item
        hoverAnchor.x = nextX
        hoverAnchor.y = nextY
        lastSwitchAt = now
        rings[Number(ringIndex)].hoverItem = item
      }
      activate(item)
    })
    item.addEventListener('focus', () => { markUserActivity(); activate(item) })
    item.addEventListener('blur', () => { if (!locked && !hoverHeldItem) activate(null); else activate(locked || hoverHeldItem) })
    item.addEventListener('click', () => {
      markUserActivity()
      if (locked === item) {
        // 取消锁定但仍在当前扇区上：保持 hover 高亮，避免点缝隙时闪灭
        locked = null
        hoverHeldItem = item
        activate(item)
        return
      }
      locked = item
      hoverHeldItem = item
      const ringIndex = item.dataset.ringIndex
      if (ringIndex !== undefined && rings[Number(ringIndex)]) rings[Number(ringIndex)].hoverItem = item
      activate(item)
    })
  }

  const onStagePointerMove = (event: PointerEvent): void => {
    markUserActivity()
    const rect = stage.getBoundingClientRect()
    pointerState.inside = true; pointerState.x = event.clientX - rect.left; pointerState.y = event.clientY - rect.top
  }
  const onStagePointerLeave = (): void => { pointerState.inside = false; if (hoverHeldItem && !locked) { hoverHeldItem = null; activate(null) } }
  stage.addEventListener('pointermove', onStagePointerMove, { passive: true })
  stage.addEventListener('pointerdown', markUserActivity, { passive: true })
  stage.addEventListener('wheel', markUserActivity, { passive: true })
  stage.addEventListener('pointerleave', onStagePointerLeave)

  function buildRingSystem(): void {
    if (!ringSystem) return
    locked = null; autoItem = null; autoOrder = []; autoIndex = 0; hoverHeldItem = null
    lastUserActivity = Date.now(); lastAutoChange = lastUserActivity - (autoInterval - autoIdleDelay); autoReleaseAt = 0
    activate(null)
    ringSystem.replaceChildren()
    ringSystem.append(createRingEffects())
    rings.length = 0; ringItems.length = 0
    let inner = ringLayout.baseRadius
    ringSettings.forEach((setting, ringIndex) => {
      const layer = svgNode('g', 'ring-layer'); layer.dataset.ringIndex = String(ringIndex); ringSystem.append(layer)
      const innerOutline = svgNode('circle', 'ring-outline'); const outerOutline = svgNode('circle', 'ring-outline')
      layer.append(innerOutline, outerOutline)
      const hit = svgNode('circle', 'ring-hit'); layer.append(hit)
      const count = setting.count
      const modules = ringModules.filter((module) => module.ring === ringIndex)
      const slotModules = new Map<number, RingModule>(); const occupied = new Set<number>()
      modules.forEach((module) => {
        const preferred = Math.round(module.position * (count - 1))
        let slot = preferred
        for (let offset = 0; occupied.has(slot) && offset < count; offset++) slot = (preferred + offset + 1) % count
        occupied.add(slot); slotModules.set(slot, module)
      })
      const randomWeight = (index: number): number => {
        const value = Math.sin((index + 1) * 12.9898 + (ringIndex + 1) * 78.233) * 43758.5453
        const unit = value - Math.floor(value)
        return 1 + (unit * 2 - 1) * setting.randomness * .78
      }
      const weights = Array.from({ length: count }, (_, index) => slotModules.get(index)?.size || (setting.distribution === 'even' ? 1 : randomWeight(index)))
      const available = 360 - count * setting.gap
      const totalWeight = weights.reduce((sum, value) => sum + value, 0)
      const spans = weights.map((weight) => available * weight / totalWeight)
      const outer = inner + setting.thickness
      const segments: RingSegment[] = []
      let angle = setting.start
      spans.forEach((span, index) => {
        segments.push({ ringIndex, index, start: angle, end: angle + span, inner, outer, content: slotModules.get(index) })
        angle += span + setting.gap
      })
      segments.filter((segment) => !segment.content).forEach((segment) => { segment.path = svgNode('path', 'ring-segment ring-decor'); layer.append(segment.path) })
      segments.filter((segment) => segment.content).forEach((segment) => {
        const content = segment.content!
        const item = svgNode('g', 'ring-item')
        item.setAttribute('tabindex', '0'); item.setAttribute('role', 'button'); item.setAttribute('aria-label', `${content.title}：${content.detail}`)
        item.dataset.mode = content.mode
        item.dataset.moduleIndex = content.index
        item.dataset.ringIndex = String(ringIndex)
        segment.item = item
        segment.path = svgNode('path', 'ring-segment'); item.append(segment.path)
        segment.hoverPath = svgNode('path', 'ring-hover-surface'); item.append(segment.hoverPath)
        segment.titleGuide = svgNode('path'); segment.titleGuide.id = `ring-title-${content.index}`; segment.titleGuide.setAttribute('fill', 'none'); segment.titleGuide.setAttribute('stroke', 'none'); item.append(segment.titleGuide)
        segment.detailGuide = svgNode('path'); segment.detailGuide.id = `ring-detail-${content.index}`; segment.detailGuide.setAttribute('fill', 'none'); segment.detailGuide.setAttribute('stroke', 'none'); item.append(segment.detailGuide)
        const title = svgNode('text', 'ring-title'); const titlePath = svgNode('textPath')
        titlePath.setAttribute('href', `#${segment.titleGuide.id}`); titlePath.setAttribute('startOffset', '50%'); titlePath.textContent = content.title
        title.append(titlePath); item.append(title)
        const detail = svgNode('text', 'ring-detail'); const detailPath = svgNode('textPath')
        detailPath.setAttribute('href', `#${segment.detailGuide.id}`); detailPath.setAttribute('startOffset', '50%'); detailPath.textContent = content.detail
        detail.append(detailPath); item.append(detail)
        layer.append(item); ringItems.push(item); bindInteractive(item)
      })
      rings.push({ group: layer, hit, innerOutline, outerOutline, segments, setting, rotation: setting.rotation, expansion: 0, hoverItem: null })
      inner = outer + ringLayout.layerGap
    })
    requestAnimationFrame(() => requestAnimationFrame(() => { if (!autoOrder.length) refreshAutoOrder() }))
  }
  buildRingSystem()

  const onKeydown = (event: KeyboardEvent): void => { markUserActivity(); if (event.key === 'Escape') { locked = null; activate(null) } }
  window.addEventListener('keydown', onKeydown)

  const updateHeldHover = (): void => {
    if (!hoverHeldItem || locked) return
    const ringIndex = Number(hoverHeldItem.dataset.ringIndex)
    const ring = rings[ringIndex]
    if (!pointerState.inside || !ring || ring.currentInner === undefined) {
      hoverHeldItem = null
      activate(null)
      return
    }
    const distance = Math.hypot(pointerState.x - (ring.currentCenterX ?? 0), pointerState.y - (ring.currentCenterY ?? 0))
    // 半径缓冲加大：膨胀动画中外缘会外扩，避免误判离开
    const buffer = 28
    if (distance < ring.currentInner - buffer || distance > (ring.currentOuter ?? 0) + buffer) {
      ring.hoverItem = null
      hoverHeldItem = null
      activate(null)
    } else if (!hoverHeldItem.classList.contains('active')) {
      activate(hoverHeldItem)
    }
  }

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
  const ringSpeedFactor = .70
  function animateRingSystem(rect: DOMRect, delta: number, time: number): void {
    if (!ringSystem) return
    const width = rect.width; const height = rect.height
    const cx = width / 2 + ringLayout.centerX; const cy = height / 2 + ringLayout.centerY
    ringSystem.setAttribute('viewBox', `0 0 ${width} ${height}`)
    let radialCursor = ringLayout.baseRadius
    rings.forEach((ring, ringIndex) => {
      const activeSegment = ring.segments.find((segment) => isSelectedItem(segment.item))
      if (activeSegment) {
        const baseMid = (activeSegment.start + activeSegment.end) / 2
        const shortest = (target: number, current: number): number => ((target - current + 540) % 360) - 180
        const targets = [90 - baseMid, 270 - baseMid]
        const targetRotation = targets.reduce((best, target) => Math.abs(shortest(target, ring.rotation)) < Math.abs(shortest(best, ring.rotation)) ? target : best, targets[0])
        ring.rotation += shortest(targetRotation, ring.rotation) * (reducedMotion.matches ? 1 : 1 - Math.exp(-delta * 5.8))
      } else {
        const rotationRate = reducedMotion.matches ? 0 : ring.setting.speed * ringSpeedFactor
        ring.rotation += delta * rotationRate
      }
      ring.setting.rotation = ring.rotation
      const targetExpansion = activeSegment ? 1 : 0
      const smoothing = reducedMotion.matches ? 1 : 1 - Math.exp(-delta * (targetExpansion ? 9 : 6))
      ring.expansion += (targetExpansion - ring.expansion) * smoothing
      const wave = reducedMotion.matches ? 0 : Math.sin(time * ringLayout.waveSpeed * 1.7 - ringIndex * 1.08) * ringLayout.wave
      const inner = radialCursor
      const thickness = Math.max(18, ring.setting.thickness + ring.expansion * ringLayout.hoverGrow + wave)
      const outer = inner + thickness
      radialCursor = outer + ringLayout.layerGap
      ring.currentInner = inner; ring.currentOuter = outer; ring.currentCenterX = cx; ring.currentCenterY = cy
      ring.innerOutline.setAttribute('cx', String(cx)); ring.innerOutline.setAttribute('cy', String(cy)); ring.innerOutline.setAttribute('r', String(inner))
      ring.outerOutline.setAttribute('cx', String(cx)); ring.outerOutline.setAttribute('cy', String(cy)); ring.outerOutline.setAttribute('r', String(outer))
      ring.hit.setAttribute('cx', String(cx)); ring.hit.setAttribute('cy', String(cy)); ring.hit.setAttribute('r', String((inner + outer) / 2)); ring.hit.setAttribute('stroke-width', String(thickness))
      ring.group.classList.toggle('has-focus', !!activeSegment)
      ring.segments.forEach((segment) => {
        const selected = isSelectedItem(segment.item)
        const start = segment.start + ring.rotation
        const end = segment.end + ring.rotation
        segment.path?.setAttribute('d', bandPath(cx, cy, inner, outer, start, end, segment.item ? ringLayout.cornerRadius : 0))
        if (segment.hoverPath) segment.hoverPath.setAttribute('d', segment.path?.getAttribute('d') || '')
        if (segment.item && segment.titleGuide && segment.detailGuide) {
          const actualMid = ((start + end) / 2) % 360
          const normalizedMid = (actualMid + 360) % 360
          const reverse = normalizedMid > 0 && normalizedMid < 180
          const inset = Math.min(5, (end - start) * .08)
          const middle = (inner + outer) / 2
          segment.titleGuide.setAttribute('d', textArc(cx, cy, middle + (selected ? 10 : 0), start + inset, end - inset, reverse))
          segment.detailGuide.setAttribute('d', textArc(cx, cy, middle - (selected ? 15 : 7), start + inset, end - inset, reverse))
        }
      })
    })
    updateHeldHover()
  }

  const stageEl = stage
  let previousOrbitTime = performance.now() / 1000
  let orbitFrameId = 0
  function animateOrbit(now: number): void {
    const rect = stageEl.getBoundingClientRect()
    const t = now / 1000
    const delta = Math.min(.05, Math.max(0, t - previousOrbitTime))
    previousOrbitTime = t
    animateRingSystem(rect, delta, t)
    updateAutoDemo()
    orbitFrameId = requestAnimationFrame(animateOrbit)
  }
  orbitFrameId = requestAnimationFrame(animateOrbit)

  cleanupFns.push(() => {
    stage.removeEventListener('pointermove', onStagePointerMove)
    stage.removeEventListener('pointerdown', markUserActivity)
    stage.removeEventListener('wheel', markUserActivity)
    stage.removeEventListener('pointerleave', onStagePointerLeave)
    window.removeEventListener('keydown', onKeydown)
    cancelAnimationFrame(orbitFrameId)
  })
}

/** 初始化 Three.js 头部模型场景与多材质切换效果，逻辑与原 index.html 保持一致 */
async function initThreeScene(): Promise<void> {
  const stage = stageRef.value
  const modelSlot = modelSlotRef.value
  const canvas = modelCanvasRef.value
  if (!stage || !modelSlot || !canvas) return

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 100)
  camera.position.set(0, 0, 6.5)
  camera.lookAt(0, 0, 0)

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' })
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.setClearColor(0xffffff, 0)

  scene.add(new THREE.HemisphereLight(0xffffff, 0x676773, 2.5))
  const key = new THREE.DirectionalLight(0xffffff, 4.2); key.position.set(2.5, 3.2, 4); scene.add(key)
  const rim = new THREE.DirectionalLight(0xa99cff, 2.8); rim.position.set(-3, 1.2, -2); scene.add(rim)

  let model: THREE.Group | null = null
  let meshes: THREE.Mesh[] = []
  let currentMode = 'default'
  let transitionElapsed = 1
  let visible = true
  let targetYaw = 0
  let currentYaw = 0
  let modelPivot: THREE.Group | null = null

  const digitalUniforms = {
    time: { value: 0 },
    baseColor: { value: new THREE.Color(0xf5f5f7) },
    gridColor: { value: new THREE.Color(0x8b7cf7) },
    modelYMin: { value: -1 },
    modelYMax: { value: 1 },
    modeFrom: { value: 0 },
    modeTo: { value: 0 },
    transition: { value: 1 },
    styleAFrom: { value: new THREE.Color(0xf5f5f7) }, styleBFrom: { value: new THREE.Color(0x8b7cf7) },
    densityFrom: { value: 11 }, flowFrom: { value: 1 }, intensityFrom: { value: 1 }, edgeFrom: { value: 0 },
    styleATo: { value: new THREE.Color(0xf5f5f7) }, styleBTo: { value: new THREE.Color(0x8b7cf7) },
    densityTo: { value: 11 }, flowTo: { value: 1 }, intensityTo: { value: 1 }, edgeTo: { value: 0 }
  }
  const digitalEffectMaterial = new THREE.ShaderMaterial({
    uniforms: digitalUniforms,
    side: THREE.DoubleSide,
    transparent: true,
    vertexShader: `
      uniform float modeFrom;
      uniform float modeTo;
      uniform float transition;
      uniform float densityFrom;
      uniform float densityTo;
      uniform float edgeFrom;
      uniform float edgeTo;
      varying vec3 vNormal;
      varying vec3 vWorldPosition;
      varying vec3 vViewDir;
      void main(){
        float blend=transition*transition*(3.0-2.0*transition);
        float pixelFrom=1.0-step(.49,abs(modeFrom-3.0));
        float pixelTo=1.0-step(.49,abs(modeTo-3.0));
        float pixelAmount=mix(pixelFrom*edgeFrom,pixelTo*edgeTo,blend);
        float snapDensity=max(4.0,mix(densityFrom,densityTo,blend));
        vec3 snapped=floor(position*snapDensity+.5)/snapDensity;
        vec3 localPosition=mix(position,snapped,pixelAmount*.72);
        vec4 worldPos=modelMatrix*vec4(localPosition,1.0);
        vWorldPosition=worldPos.xyz;
        vNormal=normalize(mat3(modelMatrix)*normal);
        vViewDir=normalize(cameraPosition-worldPos.xyz);
        gl_Position=projectionMatrix*viewMatrix*worldPos;
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 baseColor;
      uniform vec3 gridColor;
      uniform float modelYMin;
      uniform float modelYMax;
      uniform float modeFrom;
      uniform float modeTo;
      uniform float transition;
      uniform vec3 styleAFrom;
      uniform vec3 styleBFrom;
      uniform float densityFrom;
      uniform float flowFrom;
      uniform float intensityFrom;
      uniform float edgeFrom;
      uniform vec3 styleATo;
      uniform vec3 styleBTo;
      uniform float densityTo;
      uniform float flowTo;
      uniform float intensityTo;
      uniform float edgeTo;
      varying vec3 vNormal;
      varying vec3 vWorldPosition;
      varying vec3 vViewDir;

      vec3 spectrum(float t){
        return .54+.46*cos(6.28318*(vec3(.02,.35,.68)+t));
      }
      float gridLine(float value,float density,float width){
        float d=abs(fract(value*density)-.5);
        return 1.0-smoothstep(width,width+.035,d);
      }
      float hash21(vec2 p){
        p=fract(p*vec2(123.34,456.21));
        p+=dot(p,p+45.32);
        return fract(p.x*p.y);
      }
      vec3 renderStyle(float mode,vec3 normal,vec3 viewDir,float diffuse,float fresnel,float spec,float normalizedY,vec3 colorA,vec3 colorB,float density,float flow,float intensity){
        float shade=clamp(.16+diffuse*.78+spec*.28,0.0,1.0);
        float localTime=time*flow;
        vec3 iri=spectrum(fresnel*1.35+vWorldPosition.y*.12+localTime*.025);

        if(mode<.5){
          float scanPos=fract(localTime*.12),scanDist=abs(normalizedY-scanPos);
          scanDist=min(scanDist,1.0-scanDist);
          float scan=smoothstep(.24,0.0,scanDist);
          float grid=max(gridLine(vWorldPosition.x,density,.018),gridLine(vWorldPosition.y,density,.018));
          vec3 color=colorA*(.72+diffuse*.28)+vec3(1.0)*spec*.42*intensity;
          color+=iri*fresnel*.38*intensity;
          color+=mix(colorB,iri,.48)*grid*(.04+scan*.22)*intensity;
          color+=iri*scan*fresnel*.10*intensity;
          return color;
        }
        if(mode<1.5){
          float gx=gridLine(vWorldPosition.x+localTime*.018,density,.026);
          float gy=gridLine(vWorldPosition.y-localTime*.014,density,.026);
          float gz=gridLine(vWorldPosition.z+localTime*.011,density,.026);
          float wire=max(max(gx,gy),max(gz,fresnel*.62))*intensity;
          return mix(colorA,colorB,clamp(wire,0.0,1.0));
        }
        if(mode<2.5){
          float cellSize=max(4.0,16.0-density*.35);
          vec2 dotCell=mod(gl_FragCoord.xy+vec2(localTime*6.0,localTime*1.7),vec2(cellSize))-cellSize*.5;
          float radius=mix(cellSize*.46,cellSize*.10,shade)*intensity;
          float paper=smoothstep(radius-.55,radius+.55,length(dotCell));
          return mix(colorB,colorA,paper);
        }
        if(mode<3.5){
          float pixelSize=max(3.0,17.0-density*.42);
          vec2 cell=floor((gl_FragCoord.xy+vec2(localTime*10.0,localTime*3.0))/pixelSize);
          float noise=hash21(cell)*.12;
          float level=floor(clamp(shade+noise,0.0,1.0)*5.0)/4.0;
          vec3 mid=mix(colorA,colorB,.52);
          vec3 pixelColor=level<.55?mix(colorA,mid,level/.55):mix(mid,colorB,(level-.55)/.45);
          return mix(vec3(shade),pixelColor,intensity);
        }
        if(mode<4.5){
          float glyphSize=max(6.0,20.0-density*.45);
          vec2 asciiUV=fract((gl_FragCoord.xy+vec2(0.0,localTime*16.0))/glyphSize)-.5;
          vec2 asciiCell=floor((gl_FragCoord.xy+vec2(0.0,localTime*16.0))/glyphSize);
          float choice=hash21(asciiCell);
          float vertical=1.0-smoothstep(.045,.105,abs(asciiUV.x));
          float horizontal=1.0-smoothstep(.045,.105,abs(asciiUV.y));
          float diagonal=1.0-smoothstep(.05,.12,abs(asciiUV.x+asciiUV.y*.82));
          float ring=1.0-smoothstep(.05,.11,abs(length(asciiUV)-.28));
          float glyph=choice<.25?vertical:choice<.5?horizontal:choice<.75?diagonal:ring;
          glyph*=step(choice*.82,shade)*intensity;
          return mix(colorA,colorB,clamp(glyph,0.0,1.0));
        }
        if(mode<5.5){
          float sliceCoord=vWorldPosition.y*density+sin(vWorldPosition.x*density*.32)*.36-localTime*.75;
          float slice=1.0-smoothstep(.035,.12,abs(fract(sliceCoord)-.5));
          float scanPos=fract(localTime*.16);
          float scanDist=abs(normalizedY-scanPos);
          scanDist=min(scanDist,1.0-scanDist);
          float sweep=smoothstep(.16,0.0,scanDist);
          float packet=step(.74,hash21(floor(vWorldPosition.xy*density*.72)+floor(localTime*1.4)));
          vec3 base=colorA*(.50+.50*shade);
          float signal=clamp(slice*intensity+sweep*.46*intensity+packet*.16,0.0,1.0);
          return mix(base,colorB,signal)+colorA*spec*.12;
        }
        float contourCoord=(shade+vWorldPosition.y*.18+normal.x*.10)*density+localTime*.18;
        float contour=1.0-smoothstep(.035,.11,abs(fract(contourCoord)-.5));
        float cellSize=max(5.0,22.0-density*.55);
        vec2 cellUV=mod(gl_FragCoord.xy+vec2(localTime*7.0,localTime*3.0),vec2(cellSize))-cellSize*.5;
        float dataDot=1.0-smoothstep(1.05,2.0,length(cellUV));
        float radar=.5+.5*sin(normalizedY*18.0-localTime*2.2);
        float signal=clamp(contour*intensity+dataDot*radar*.72*intensity,0.0,1.0);
        return mix(colorA,colorB,signal)+colorB*fresnel*.10*intensity;
      }
      void main(){
        vec3 normal=normalize(vNormal),viewDir=normalize(vViewDir);
        vec3 lightDir=normalize(vec3(-.45,.72,.75));
        float diffuse=max(dot(normal,lightDir),0.0);
        float fresnel=pow(1.0-max(dot(normal,viewDir),0.0),2.35);
        float spec=pow(max(dot(normal,normalize(lightDir+viewDir)),0.0),32.0);

        float yRange=max(.001,modelYMax-modelYMin);
        float normalizedY=clamp((vWorldPosition.y-modelYMin)/yRange,0.0,1.0);
        vec3 fromColor=renderStyle(modeFrom,normal,viewDir,diffuse,fresnel,spec,normalizedY,styleAFrom,styleBFrom,densityFrom,flowFrom,intensityFrom);
        vec3 toColor=renderStyle(modeTo,normal,viewDir,diffuse,fresnel,spec,normalizedY,styleATo,styleBTo,densityTo,flowTo,intensityTo);
        float blend=transition*transition*(3.0-2.0*transition);
        vec3 color=mix(fromColor,toColor,blend);
        float pixelFrom=1.0-step(.49,abs(modeFrom-3.0));
        float pixelTo=1.0-step(.49,abs(modeTo-3.0));
        float pixelEdge=mix(pixelFrom*edgeFrom,pixelTo*edgeTo,blend);
        vec2 edgeCell=floor((gl_FragCoord.xy+vec2(time*5.0,time*2.0))/9.0);
        float edgeNoise=hash21(edgeCell);
        float breakup=step(edgeNoise,smoothstep(.28,.92,fresnel)*.78);
        float alpha=1.0-pixelEdge*breakup*.76;
        gl_FragColor=vec4(color,alpha);
      }
    `
  })

  const onStagePointerMoveYaw = (event: PointerEvent): void => {
    const rect = stage.getBoundingClientRect()
    const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - .5) * 2))
    const turn = 11
    targetYaw = THREE.MathUtils.degToRad(x * turn)
  }
  const onStagePointerLeaveYaw = (): void => { targetYaw = 0 }
  stage.addEventListener('pointermove', onStagePointerMoveYaw)
  stage.addEventListener('pointerleave', onStagePointerLeaveYaw)

  const styleProfiles: Record<string, { a: string, b: string, density: number, flow: number, intensity: number, edge: number }> = {
    default: { a: '#ffffff', b: '#ffffff', density: 13, flow: 1.66, intensity: 2, edge: 0 },
    wire: { a: '#ffffff', b: '#7a7a7a', density: 13, flow: 1.99, intensity: 2, edge: 0 },
    halftone: { a: '#f8f8f8', b: '#101012', density: 11, flow: 1, intensity: 1, edge: 0 },
    pixel: { a: '#d6d6d6', b: '#f9f9fe', density: 14, flow: 1, intensity: 1, edge: .82 },
    ascii: { a: '#ffffff', b: '#000000', density: 30, flow: 2.22, intensity: 2, edge: 0 },
    scan: { a: '#ffffff', b: '#ffffff', density: 30, flow: .52, intensity: 1.83, edge: .62 },
    topology: { a: '#ffffff', b: '#b0b0b0', density: 12, flow: 1.5, intensity: 2, edge: .52 }
  }
  const applyProfile = (suffix: 'From' | 'To', profile: typeof styleProfiles.default): void => {
    ;(digitalUniforms as any)[`styleA${suffix}`].value.set(profile.a)
    ;(digitalUniforms as any)[`styleB${suffix}`].value.set(profile.b)
    ;(digitalUniforms as any)[`density${suffix}`].value = profile.density
    ;(digitalUniforms as any)[`flow${suffix}`].value = profile.flow
    ;(digitalUniforms as any)[`intensity${suffix}`].value = profile.intensity
    ;(digitalUniforms as any)[`edge${suffix}`].value = profile.edge
  }

  function resize(): void {
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5))
    renderer.setSize(rect.width, rect.height, false)
    canvas.style.imageRendering = 'auto'
    camera.aspect = rect.width / rect.height
    camera.updateProjectionMatrix()
  }

  function setMaterial(mode: string): void {
    const modeIds: Record<string, number> = { default: 0, wire: 1, halftone: 2, pixel: 3, ascii: 4, scan: 5, topology: 6 }
    const nextMode = modeIds[mode] ?? 0
    const useTarget = digitalUniforms.transition.value > .5
    const currentVisual = useTarget ? digitalUniforms.modeTo.value : digitalUniforms.modeFrom.value
    digitalUniforms.modeFrom.value = currentVisual
    if (useTarget) {
      digitalUniforms.styleAFrom.value.copy(digitalUniforms.styleATo.value)
      digitalUniforms.styleBFrom.value.copy(digitalUniforms.styleBTo.value)
      digitalUniforms.densityFrom.value = digitalUniforms.densityTo.value
      digitalUniforms.flowFrom.value = digitalUniforms.flowTo.value
      digitalUniforms.intensityFrom.value = digitalUniforms.intensityTo.value
      digitalUniforms.edgeFrom.value = digitalUniforms.edgeTo.value
    }
    digitalUniforms.modeTo.value = nextMode
    applyProfile('To', styleProfiles[mode] || styleProfiles.default)
    digitalUniforms.transition.value = 0
    transitionElapsed = 0
    currentMode = mode
    meshes.forEach((mesh) => { mesh.material = digitalEffectMaterial })
  }

  const modeObserver = new MutationObserver(() => setMaterial(modelSlot.dataset.mode || 'default'))
  modeObserver.observe(modelSlot, { attributes: true, attributeFilter: ['data-mode'] })
  const resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(canvas)
  let visibleFlag = true
  const intersectionObserver = new IntersectionObserver((entries) => { visibleFlag = entries[0]?.isIntersecting ?? true; visible = visibleFlag }, { threshold: .01 })
  intersectionObserver.observe(modelSlot)

  // 跟随 Vite base（PUBLIC_PATH），避免部署到子路径时 /delivery 打到站点根目录拿到 HTML
  const assetBase = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`
  const dracoLoader = new DRACOLoader()
  dracoLoader.setDecoderPath(`${assetBase}delivery/vendor/draco/`)
  const modelLoader = new GLTFLoader()
  modelLoader.setDRACOLoader(dracoLoader)
  let disposed = false
  modelLoader.load(`${assetBase}delivery/assets/head.glb`, (gltf: { scene: THREE.Group }) => {
    if (disposed) return
    model = gltf.scene
    const box = new THREE.Box3().setFromObject(model)
    const center = box.getCenter(new THREE.Vector3()); const size = box.getSize(new THREE.Vector3())
    const scale = 2.05 / Math.max(size.x, size.y, size.z)
    model.scale.setScalar(scale)
    model.position.copy(center).multiplyScalar(-scale)
    digitalUniforms.modelYMin.value = -size.y * scale / 2
    digitalUniforms.modelYMax.value = size.y * scale / 2
    model.traverse((object: THREE.Object3D) => {
      if (object instanceof THREE.Mesh) {
        object.material = digitalEffectMaterial
        meshes.push(object)
        object.frustumCulled = false
      }
    })
    modelPivot = new THREE.Group()
    modelPivot.add(model)
    scene.add(modelPivot)
    modelPivot.updateMatrixWorld(true)
    setMaterial(modelSlot.dataset.mode || 'default')
    modelSlot.classList.add('loaded')
  }, undefined, (error: unknown) => { console.error('[DigitalHumanTechRing] 数字人模型加载失败：', error) })

  const clock = new THREE.Clock()
  let renderFrameId = 0
  function render(): void {
    renderFrameId = requestAnimationFrame(render)
    if (!visible) return
    const delta = Math.min(clock.getDelta(), .05)
    digitalUniforms.time.value += delta
    transitionElapsed += delta
    digitalUniforms.transition.value = Math.min(1, transitionElapsed / (620 / 1000))
    currentYaw += (targetYaw - currentYaw) * .075
    if (modelPivot) modelPivot.rotation.y = currentYaw
    // 每帧把场景画到 canvas（漏掉会导致模型加载了却不显示）
    renderer.render(scene, camera)
  }
  resize(); render()

  cleanupFns.push(() => {
    disposed = true
    stage.removeEventListener('pointermove', onStagePointerMoveYaw)
    stage.removeEventListener('pointerleave', onStagePointerLeaveYaw)
    modeObserver.disconnect()
    resizeObserver.disconnect()
    intersectionObserver.disconnect()
    cancelAnimationFrame(renderFrameId)
    scene.traverse((object: THREE.Object3D) => { if (object instanceof THREE.Mesh) object.geometry.dispose() })
    digitalEffectMaterial.dispose()
    dracoLoader.dispose()
    renderer.dispose()
    meshes = []
    void currentMode
  })
}
</script>

<template>
  <section ref="stageRef" class="tech-ring-stage" data-layout="rings" aria-label="数字人核心技术交互预览">
    <svg ref="ringSystemRef" class="tech-ring-system" data-line-mode="full" role="group" aria-label="数字人技术分段圆环" />
    <div ref="modelSlotRef" class="tech-ring-model-slot" data-mode="default">
      <canvas ref="modelCanvasRef" class="tech-ring-model-canvas" aria-label="交互式三维头部模型" />
    </div>
  </section>
</template>

<style scoped>
.tech-ring-stage {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  height: 780px;
  min-height: 780px;
  background: transparent;
}

.tech-ring-model-slot {
  --mx: 0px;
  --my: 0px;
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: 290px;
  height: 390px;
  transform: translate3d(calc(-50% + var(--mx)), calc(-50% + var(--my)), 0);
  transform-style: preserve-3d;
  transition: transform .16s cubic-bezier(.2, .8, .2, 1);
}

.tech-ring-model-canvas {
  position: absolute;
  inset: -32px;
  width: calc(100% + 64px);
  height: calc(100% + 64px);
  opacity: 0;
  filter: blur(0);
  transition: opacity .45s ease, filter .55s ease;
  pointer-events: none;
}

.tech-ring-model-slot.loaded .tech-ring-model-canvas { opacity: 1; }
.tech-ring-model-slot[data-mode="pixel"] .tech-ring-model-canvas { filter: blur(.7px) contrast(1.06); }

.tech-ring-system {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

:deep(.ring-segment) { vector-effect: non-scaling-stroke; stroke-linejoin: round; transition: opacity .36s ease, stroke .36s ease; }
:deep(.ring-outline) { fill: none; stroke: #d9dade; stroke-width: .5px; vector-effect: non-scaling-stroke; pointer-events: none; }
:deep(.ring-hit) { fill: none; stroke: transparent; pointer-events: stroke; }
:deep(.ring-decor) { fill: rgba(255, 255, 255, .01); stroke: none; pointer-events: none; }
.tech-ring-system[data-line-mode="full"] :deep(.ring-decor) { stroke: #d9dade; stroke-width: .5px; }
:deep(.ring-item) { pointer-events: auto; cursor: pointer; outline: none; opacity: 1; transition: opacity .38s ease; }
:deep(.ring-item .ring-segment) { fill: #0c0c0f; stroke: #fff; stroke-width: .5px; }
:deep(.ring-hover-surface) {
  fill: #8932FF;
  stroke: url(#ring-hover-edge);
  stroke-width: 1.2px;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
  opacity: 0;
  transition: opacity .3s cubic-bezier(.2, .8, .2, 1);
}
/* 有选中项时只用 .active，避免交界处 :hover 与 active 双高亮闪动 */
:deep(.ring-item.active .ring-hover-surface),
:deep(.ring-item:focus-visible .ring-hover-surface) { opacity: 1; }
.tech-ring-stage:not(.has-active) :deep(.ring-item:hover .ring-hover-surface) { opacity: 1; }
:deep(.ring-item:focus-visible .ring-segment) { stroke: #7064ff; stroke-width: 2px; }
:deep(.ring-title),
:deep(.ring-detail) { fill: #fff; pointer-events: none; text-anchor: middle; dominant-baseline: middle; }
:deep(.ring-title) { font-size: 14px; font-weight: 720; letter-spacing: .065em; }
:deep(.ring-detail) { font-size: 10px; font-weight: 520; letter-spacing: .035em; opacity: 0; transition: opacity .32s ease; }
:deep(.ring-item.active .ring-detail),
:deep(.ring-item:focus-visible .ring-detail) { opacity: .78; }
.tech-ring-stage:not(.has-active) :deep(.ring-item:hover .ring-detail) { opacity: .78; }

@media (max-width: 800px) {
  .tech-ring-stage { height: auto; min-height: 520px; }
}

@media (prefers-reduced-motion: reduce) {
  .tech-ring-model-slot { transition: none; }
}
</style>
